import { ApiError, getJson, postJsonWithStatus } from './client';
import { ApiEnvelope, isApiEnvelope } from './migrations';

// FD-372 — the console half of the FD-ADR-035 canary arc.
//
// Two surfaces, both consumed read/act-only:
//
//   * `GET  /v1/migrations/{id}/failed-devices` (FD-369) — the H2 retry set:
//     devices whose terminal outcome is `salvaged` or `faulted`. `aborted` is
//     EXCLUDED BY DESIGN server-side: it is a bulk stamp for devices someone
//     deliberately stopped, and sweeping them into a retry set would migrate
//     devices an operator chose to stop.
//   * `POST /v1/migrations/{id}/canary-release` (FD-371) — the audited operator
//     release of the first-device canary hold. The FD-ADR-027 override
//     treatment: reason required, who/when from the EasyAuth principal
//     server-side, a `canary.released` event. The release clears the HOLD; it
//     rewrites nothing — the canary's outcome stays on its device row.

/** The H2 retry vocabulary. `aborted` never appears here — see above. */
export type FailedDeviceOutcome = 'salvaged' | 'faulted';

export interface FailedDevice {
  serialNumber: string;
  jobId: string;
  terminalOutcome: FailedDeviceOutcome;
  terminalUtc: string;
  /**
   * The actionId the device was executing when it went terminal, joined from
   * the open SalvagedDevices registry record. `null` when no open record
   * exists (faulted devices, or a salvage already closed by a later
   * successful migration).
   */
  failedActionId: string | null;
  /**
   * An open FD-ADR-033 salvage record — the device's recovery-admin password
   * is RETAINED until it completes a later migration, and is retrievable via
   * the audited console reveal.
   */
  salvageRecordOpen: boolean;
}

/** Unwrapped `data` payload of GET /v1/migrations/{id}/failed-devices. */
export interface FailedDevicesResponse {
  migrationId: string;
  count: number;
  failedDevices: FailedDevice[];
}

export async function getFailedDevices(migrationId: string): Promise<FailedDevicesResponse> {
  const envelope = await getJson<ApiEnvelope<FailedDevicesResponse>>(
    `/v1/migrations/${encodeURIComponent(migrationId)}/failed-devices`,
  );
  return envelope.data;
}

/**
 * `data` payload of POST /v1/migrations/{id}/canary-release.
 * `repeat` is present (true) only when the hold was ALREADY released — the
 * endpoint is idempotent and reports the ORIGINAL stamp, never a restamp.
 */
export interface CanaryReleaseResult {
  migrationId: string;
  released: boolean;
  releasedUtc: string;
  releasedBy: string | null;
  repeat?: boolean;
}

export async function releaseCanary(
  migrationId: string,
  reason: string,
): Promise<CanaryReleaseResult> {
  const { body } = await postJsonWithStatus<ApiEnvelope<CanaryReleaseResult>, { reason: string }>(
    `/v1/migrations/${encodeURIComponent(migrationId)}/canary-release`,
    { reason },
  );
  return body.data;
}

/**
 * The machine codes canary-release returns. Named because they are different
 * operator situations:
 *
 * - `CANARY_NOT_ACTIVE` (409) — nothing to release: canary mode is off (or a
 *   pre-FD-371 row). NOT a failure to alarm on — the graceful rendering is
 *   "there is no hold here", plus a refetch so the panel stops offering the
 *   button.
 * - `REASON_REQUIRED` (400) — the client let an empty reason through; the
 *   server is the backstop.
 * - `PRINCIPAL_UNRESOLVED` (400) — the release must be attributable and the
 *   EasyAuth principal could not be resolved. A deployment/auth problem, not a
 *   campaign one.
 */
const CANARY_ERROR_CODES = [
  'CANARY_NOT_ACTIVE',
  'REASON_REQUIRED',
  'PRINCIPAL_UNRESOLVED',
] as const;

export type CanaryErrorCode = (typeof CANARY_ERROR_CODES)[number] | 'UNKNOWN';

const KNOWN_CANARY_ERROR_CODES: ReadonlySet<string> = new Set(CANARY_ERROR_CODES);

export function canaryErrorCode(err: unknown): CanaryErrorCode {
  if (!(err instanceof ApiError)) return 'UNKNOWN';
  const code = isApiEnvelope(err.body) ? err.body.error?.code : undefined;
  return typeof code === 'string' && KNOWN_CANARY_ERROR_CODES.has(code)
    ? (code as CanaryErrorCode)
    : 'UNKNOWN';
}
